import type { ReactNode } from 'react';
import { StatusChip, JurisdictionBadge } from '../../tax-settings/JurisdictionBadge';
import { INFORMATIONAL_NOTICE } from '../../tax-settings/checklistContent';
import { TaxSetupStatusPanel } from '../../tax-settings/TaxSetupStatusPanel';
import { useJurisdictionCatalog } from '../../tax-settings/useJurisdictionCatalog';
import { SectionFrame } from '../components/SectionFrame';
import type { JurisdictionValueDto } from '../types';
import { JurisdictionValue } from './JurisdictionValue';
import type { TaxSectionProps } from './taxProps';

function Cell({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="eo-fact">
      <span className="eo-fact__label">{label}</span>
      {children}
    </div>
  );
}

/** A state value with its availability badge (looked up in the shared catalog by name; no badge when the catalog does not know it). */
function StateCell({ label, item, testId }: { label: string; item: JurisdictionValueDto; testId: string }) {
  const catalog = useJurisdictionCatalog();
  const entry = item.status === 'Verified' ? catalog.findByName(item.value) : undefined;
  return (
    <Cell label={label}>
      <span className="ts-inline">
        <JurisdictionValue item={item} testId={testId} />
        {entry && <JurisdictionBadge availability={entry.availability} />}
      </span>
    </Cell>
  );
}

/** The local jurisdictions are never determined yet: shown as "Not determined yet" with Needs review, never as "none". */
function LocalCell({ label }: { label: string }) {
  return (
    <Cell label={label}>
      <span className="ts-inline">
        <span className="eo-jv eo-jv--muted">Not determined yet</span>
        <StatusChip status="NeedsReview" />
      </span>
    </Cell>
  );
}

/** Tax Jurisdiction Summary (Figma 07, 08): read-only. Every value is real data or an honest Unknown / Pending review; nothing says payroll is ready. */
export function TaxSummarySection({ taxes, next, back }: TaxSectionProps) {
  const s = taxes.summary;
  const multi = s.multiState;
  return (
    <SectionFrame title="Tax Jurisdiction Summary" onSubmit={next} onBack={back}>
      {multi.status === 'ReviewRequired' && (
        <p className="eo-banner eo-banner--danger" role="alert" data-testid="multistate-banner">
          {multi.message}
        </p>
      )}
      {multi.status === 'Unresolved' && (
        <p className="eo-banner eo-banner--info" data-testid="multistate-unresolved">
          {multi.message}
        </p>
      )}

      {taxes.taxSetup && <TaxSetupStatusPanel status={taxes.taxSetup} />}

      <div className="eo-facts">
        <Cell label="Federal">
          <span className="eo-jv" data-testid="summary-federal">
            {s.federal}
          </span>
        </Cell>
        <StateCell label="Work State" item={s.workState} testId="summary-work-state" />
        <StateCell label="Residence State" item={s.residenceState} testId="summary-residence-state" />
        <Cell label="State Reciprocity">
          <span className="eo-jv" data-testid="summary-reciprocity">
            {s.reciprocityLabel}
          </span>
        </Cell>
        <LocalCell label="Work Local Tax" />
        <LocalCell label="Residence Local Tax" />
        <Cell label="SUI">
          <JurisdictionValue item={s.sui} testId="summary-sui" />
        </Cell>
        <Cell label="SDI/PFL">
          <JurisdictionValue item={s.sdiPfl} />
        </Cell>
      </div>

      {multi.unresolved.length > 0 && (
        <ul className="eo-unresolved" data-testid="summary-unresolved">
          {multi.unresolved.map((u) => (
            <li key={u}>{u}</li>
          ))}
        </ul>
      )}
      <p className="eo-note" data-testid="summary-readiness-note">
        {s.payrollReadinessNote}
      </p>
      <p className="eo-note">{INFORMATIONAL_NOTICE}</p>
    </SectionFrame>
  );
}
